import type { NavigationPosition } from '@unifyapps/defs/types/interface';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { useTranslation } from '@unifyapps/i18n/client';
import SvgSidebarLeft from '@unifyapps/icons/solid/SidebarLeft';
import { useAdaptFileUrl } from '@unifyapps/network/hooks/useAdaptFileUrl';
import { Box } from '@unifyapps/ui/components/Box';
import { IconButton } from '@unifyapps/ui/components/IconButton';
import { Stack } from '@unifyapps/ui/components/Stack';
import { slowCn } from '@unifyapps/ui/lib/utils';
import clsx from 'clsx';
import { AnimatePresence } from 'motion/react';
import { create as createMotionComponent } from 'motion/react-m';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useRouter } from '../../../../navigation';
import { NavigationIcon } from '../../components/NavigationIcon';
import { UA_SIDE_NAV_APP_INFO_ICON } from '../../constants';
import { isPlatformSideNav, isTopNavigation } from '../../helpers';
import {
  type NavItem,
  type NavigationItemLabelVariantEnum,
  NavigationItemSelectionVariantEnum,
} from '../../types';
import { resolveOpenInNewTab } from '../../utils/redirection';

const MotionNavigationIcon = createMotionComponent(NavigationIcon);

export interface LogoInfo {
  small: string;
  large?: string;
  // icon getting cut due to svg?
  hasExtraSpace?: boolean;
}

interface AppInfoProps {
  expanded: boolean;
  app: { name: string; logo: LogoInfo };
  logoNavigation?: NavItem;
  className?: string;
  labelVariant?: NavigationItemLabelVariantEnum;
  position?: NavigationPosition;
  setExpanded?: Dispatch<SetStateAction<boolean>>;
}

function AppInfo(props: AppInfoProps) {
  const router = useRouter();
  const { t } = useTranslation(['common']);
  const { expanded, app, logoNavigation, className, labelVariant, position, setExpanded } = props;
  const hasExtraSpace = app.logo.hasExtraSpace;
  const adaptFileUrl = useAdaptFileUrl();

  const iconUrl = useMemo(
    () => adaptFileUrl(expanded ? app.logo.large || app.logo.small : app.logo.small),
    [expanded, app.logo.large, app.logo.small, adaptFileUrl],
  );

  const onClick = useEventCallback((e: React.MouseEvent) => {
    if (logoNavigation?.type === 'URL' && logoNavigation.url) {
      // logo navigates; stop the click from reaching the rail's click-to-toggle handler.
      e.stopPropagation();
      if (logoNavigation.isInvalidUrl) {
        e.preventDefault();
        e.stopPropagation();
        console.error('Dynamic Params not passed properly', logoNavigation);
        return;
      }

      if (resolveOpenInNewTab(logoNavigation)) {
        window.open(logoNavigation.url, '_blank', 'noreferrer noopener');
      } else {
        router.push(logoNavigation.url);
      }
    }
  });

  const _isPlatformSideNav = isPlatformSideNav(labelVariant);
  const _isTopNavigation = isTopNavigation(position);

  const handleToggleExpanded = useEventCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    // header root has an onClick for logo navigation; stop it so the toggle doesn't also navigate.
    e.stopPropagation();
    setExpanded?.((prev) => !prev);
    // a pointer click leaves the button focused; in the collapsed rail focus-within keeps the toggle
    // pinned over the logo. blur on pointer toggles only (detail>0) so the logo returns; keyboard
    // activation (detail===0) keeps focus so the control stays visible for keyboard users.
    if (e.detail !== 0) e.currentTarget.blur();
  });

  const showToggle = _isPlatformSideNav && Boolean(setExpanded);

  return (
    <Stack
      className={slowCn(
        'overflow-hidden',
        {
          'px-lg': !hasExtraSpace || (_isPlatformSideNav && !expanded),
          'ps-lg pe-[11px]': hasExtraSpace,
          'justify-center': !expanded && !_isPlatformSideNav,
          'cursor-pointer': logoNavigation?.type === 'URL' && logoNavigation.url,
          'shrink-0': _isPlatformSideNav,
          'ps-[12px] pe-[8px]': _isPlatformSideNav && expanded,
          // paired with the rail root's pt (16 collapsed / 6 expanded) and the logo height
          // (32 / 44): both states total 56px so the nav below doesn't jump on toggle.
          'pb-[8px]': _isPlatformSideNav && !expanded,
          'pb-[6px]': _isPlatformSideNav && expanded,
          'pb-lg': !_isPlatformSideNav && !_isTopNavigation,
          // expanded platform header is a row: logo at start, collapse toggle at end.
          'justify-between items-center': _isPlatformSideNav && expanded,
          // collapsed rail: logo centered, expand toggle revealed on hover/focus of the rail
          // (the named group/sidenav lives on the nav root, not here — a plain `group` collides
          // with the screen-level `.group` on <body> and would reveal on any page hover).
          'relative justify-center items-center': _isPlatformSideNav && !expanded,
          // the hover toggle is bigger than the logo it covers and sits 4px above centre, so it
          // pokes past this header's box — the default clip would shave its top edge.
          'overflow-visible': _isPlatformSideNav && !expanded,
        },
        className,
      )}
      direction="row"
      onClick={onClick}
    >
      <Box
        className={clsx(
          'overflow-hidden shrink-0 transition-opacity duration-200',
          UA_SIDE_NAV_APP_INFO_ICON,
          {
            // expanded platform header renders the full lockup (mark + tagline) at 44px — the
            // height absorbed from the reduced paddings so the header block stays 56px total.
            // min-w reserves the lockup's usual footprint so the box doesn't collapse to zero and
            // reflow when the (freshly swapped) large logo is still decoding — but it stays w-auto
            // so a wider tenant logo grows instead of being squashed.
            'h-11 min-w-[140px]': _isPlatformSideNav && expanded,
            'h-8': !_isPlatformSideNav || !expanded,
            'w-8': _isPlatformSideNav && !expanded,
            // hide the logo behind the toggle while the collapsed rail is hovered. (focus-within is
            // intentionally NOT used — a focused nav item would otherwise hide the logo persistently.)
            'group-hover/sidenav:opacity-0': _isPlatformSideNav && showToggle && !expanded,
          },
        )}
      >
        <AnimatePresence mode="wait">
          <MotionNavigationIcon
            className={clsx('shrink-0', {
              'h-11': _isPlatformSideNav && expanded,
              'h-8': !_isPlatformSideNav || !expanded,
              'w-8': _isPlatformSideNav && !expanded,
              'w-auto': !_isPlatformSideNav || expanded,
            })}
            iconUrl={iconUrl}
            isActive={false}
            label={app.name}
            selectionVariant={NavigationItemSelectionVariantEnum.Stroke}
          />
        </AnimatePresence>
      </Box>
      {showToggle ? (
        <IconButton
          aria-expanded={expanded}
          aria-label={expanded ? t('common:CollapseSidebar') : t('common:ExpandSidebar')}
          // the green island doesn't use the neutral-ghost fg/hover tokens (they'd be dark/invisible on
          // the platform navbar background), so keep the toggle on the same token path as platform nav items.
          className={clsx(
            'ua-platform-side-nav-toggle-button !rounded-md group/NavItemLinkDefault transition duration-200',
            {
              // collapsed: 36px target overlaying the centered logo box, 9px padding around the
              // 18px glyph, lifted 4px so it reads as centred on the logo (the header's padding is
              // top-heavy, so plain m-auto centring sits low). Hidden until the rail is hovered;
              // focus-visible keeps it reachable for keyboard users without revealing on every
              // focused nav item.
              '!size-9 !p-[9px] absolute inset-0 m-auto -translate-y-1 opacity-0 group-hover/sidenav:opacity-100 focus-visible:opacity-100':
                !expanded,
              '!size-8 !p-md': expanded,
            },
          )}
          color="neutral"
          // panel-left glyph is the sidebar toggle in both states (RTL flip is baked into the icon);
          // the logo→toggle swap on the collapsed rail conveys the open/close affordance, not the glyph.
          Icon={SvgSidebarLeft}
          // collapsed rail matches the platform nav items' 18px glyph; the expanded header keeps
          // the smaller 16px one that fits its 32px button.
          iconClassName={clsx('ua-platform-side-nav-toggle-icon', {
            '!size-[18px]': !expanded,
            '!size-4': expanded,
          })}
          onClick={handleToggleExpanded}
          size="sm"
          variant="ghost"
        />
      ) : null}
    </Stack>
  );
}

export default AppInfo;
